/**
 * Uploads one video straight to S3, in parts, using the API only to start,
 * sign and finish it (see /api/scraper/video-uploads on the server).
 *
 * Why parts: videos run to 2 GB and are sent from roofs and basements. As one
 * request, any dropped connection would restart the whole file. In 10 MB parts,
 * a failed part is simply sent again, and an upload cut off by the app closing
 * resumes from the parts S3 already has (the upload's key/id are kept in
 * localStorage until it finishes).
 */
import { apiUrl } from '../config/api';

export const VIDEO_MAX_BYTES = 2 * 1024 ** 3;
const PART_CONCURRENCY = 3;
const PART_ATTEMPTS = 4;
const URL_BATCH = 50;
const RESUME_PREFIX = 'capturify-video-upload:';

const resumeKey = (id) => `${RESUME_PREFIX}${id}`;

function readResume(id) {
  try { return JSON.parse(localStorage.getItem(resumeKey(id)) || 'null'); } catch { return null; }
}
function writeResume(id, value) {
  try { localStorage.setItem(resumeKey(id), JSON.stringify(value)); } catch { /* resume is a bonus */ }
}
export function clearResume(id) {
  try { localStorage.removeItem(resumeKey(id)); } catch { /* ignore */ }
}

async function api(path, token, options = {}) {
  const res = await fetch(apiUrl(path), {
    ...options,
    headers: { Authorization: `Bearer ${token}`, ...(options.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.detail || data.message || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data;
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * @param {object} p
 * @param {string} p.id          stable id of this upload (the queue item id) — keys resume state
 * @param {File}   p.file
 * @param {Blob}   [p.posterBlob]
 * @param {number} [p.durationMs]
 * @param {object} p.target      { clientId, clientName, clientAddress, inspectionDate }
 * @param {string} p.token
 * @param {(clientId:number)=>void} [p.onClientId]  called as soon as the project is known
 * @param {(fraction:number)=>void} [p.onProgress]
 * @returns {Promise<{clientId:number, imageId:number}>}
 */
export async function uploadVideo({ id, file, posterBlob, durationMs, target, token, onClientId, onProgress }) {
  if (file.size > VIDEO_MAX_BYTES) throw new Error('Video is over 2 GB');
  const startedAt = Date.now();

  // 1. Start — or pick up an upload this device already began.
  let session = readResume(id);
  let done = new Set();
  if (session) {
    try {
      const { parts } = await api(
        `/api/scraper/video-uploads/parts?key=${encodeURIComponent(session.key)}&uploadId=${encodeURIComponent(session.uploadId)}`,
        token
      );
      done = new Set(parts.filter((p) => p.size > 0).map((p) => p.partNumber));
    } catch (err) {
      if (err.status !== 404) throw err;
      session = null; // expired or aborted: start over
    }
  }
  if (!session) {
    const started = await api('/api/scraper/video-uploads', token, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contentType: file.type || 'video/mp4',
        byteSize: file.size,
        clientId: target.clientId || null,
        clientName: target.clientName || null,
        clientAddress: target.clientAddress || null,
      }),
    });
    session = {
      key: started.key,
      uploadId: started.uploadId,
      clientId: started.clientId,
      partSize: started.partSize,
      partCount: started.partCount,
    };
    writeResume(id, session);
  }
  onClientId?.(session.clientId);

  // 2. Send every part S3 doesn't have yet, a few at a time.
  const { partSize, partCount } = session;
  const todo = [];
  for (let n = 1; n <= partCount; n += 1) if (!done.has(n)) todo.push(n);
  let sentBytes = [...done].reduce((sum, n) => sum + Math.min(partSize, file.size - (n - 1) * partSize), 0);
  onProgress?.(sentBytes / file.size);

  const urls = {};
  const urlFor = async (n) => {
    if (!urls[n]) {
      const batch = todo.filter((m) => !urls[m]).slice(0, URL_BATCH);
      if (!batch.includes(n)) batch.unshift(n);
      const res = await api('/api/scraper/video-uploads/part-urls', token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: session.key, uploadId: session.uploadId, partNumbers: batch }),
      });
      Object.assign(urls, res.urls);
    }
    return urls[n];
  };

  const sendPart = async (n) => {
    const blob = file.slice((n - 1) * partSize, Math.min(n * partSize, file.size));
    for (let attempt = 1; ; attempt += 1) {
      try {
        const res = await fetch(await urlFor(n), { method: 'PUT', body: blob });
        if (res.status === 403) { delete urls[n]; throw new Error('part link expired'); }
        if (!res.ok) throw new Error(`part ${n}: HTTP ${res.status}`);
        sentBytes += blob.size;
        onProgress?.(sentBytes / file.size);
        return;
      } catch (err) {
        if (attempt >= PART_ATTEMPTS) throw err;
        await sleep(1000 * 2 ** attempt); // 2 s, 4 s, 8 s — rides out a signal dip
      }
    }
  };

  let next = 0;
  const worker = async () => {
    while (next < todo.length) {
      const n = todo[next];
      next += 1;
      await sendPart(n);
    }
  };
  await Promise.all(Array.from({ length: Math.min(PART_CONCURRENCY, todo.length) }, worker));

  // 3. Finish: S3 joins the parts and the video is saved to the project.
  const form = new FormData();
  form.append('key', session.key);
  form.append('uploadId', session.uploadId);
  form.append('clientId', String(session.clientId));
  if (target.inspectionDate) form.append('inspectionDate', target.inspectionDate);
  if (durationMs) form.append('durationMs', String(durationMs));
  form.append('uploadMs', String(Date.now() - startedAt));
  if (posterBlob) form.append('poster', posterBlob, 'poster.jpg');
  const result = await api('/api/scraper/video-uploads/complete', token, { method: 'POST', body: form });
  clearResume(id);
  onProgress?.(1);
  return { clientId: result.clientId, imageId: result.imageId };
}

/** Give up on a video for good: S3 drops whatever parts it already had. */
export async function abandonVideo(id, token) {
  const session = readResume(id);
  clearResume(id);
  if (!session || !token) return;
  try {
    await api('/api/scraper/video-uploads/abort', token, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key: session.key, uploadId: session.uploadId }),
    });
  } catch { /* parts left behind are harmless */ }
}
